import { forwardRef, useCallback, useReducer, useRef, useState } from 'react'
import type { TextInput } from 'react-native'

import * as S from './root.styles'

import {
  DisplayNames,
  InputMultiSelectProvider,
  type ScopedProps,
} from '../../input-multi-select.context'

import type {
  InputMultiSelectControlPosition,
  InputMultiSelectOption,
  InputMultiSelectProps,
} from '../../input-multi-select.types'
import { useSplitChildren } from './use-split-children'

import {
  InputMultiSelectActions,
  inputMultiSelectReducer,
} from '../../input-multi-select-reducer'

import { InputMultiSelectPortal } from '../portal'

export const InputMultiSelectRoot = forwardRef<
  React.ElementRef<typeof S.Container>,
  InputMultiSelectProps
>((externalProps, ref) => {
  const {
    __scopeInputMultiSelect,
    children,
    size = 'ml',
    isError = false,
    isDisabled = false,
    isReadOnly = false,
    full = false,
    options,
    onSelectionChange,
    testID,
    ...props
  } = externalProps as ScopedProps<InputMultiSelectProps>

  if (!options.length) {
    throw new Error('No options provided for the multi-select input.')
  }

  const [isFocused, setIsFocused] = useState(false)

  const [controlLayoutInfo, setControlLayoutInfo] =
    useState<InputMultiSelectControlPosition>({
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    })

  const [confirmedOptionsSelected, dispatch] = useReducer(
    inputMultiSelectReducer,
    []
  )

  const [temporaryOptionsSelected, setTemporaryOptionsSelected] = useState<
    InputMultiSelectOption[]
  >([])

  const hiddenInputRef = useRef<TextInput | null>(null)

  const { clonedDropdownElement, otherElements } = useSplitChildren(children)

  function handleTempOptionSelected(
    actionType: 'add' | 'discard',
    newOption: InputMultiSelectOption
  ) {
    setTemporaryOptionsSelected(
      actionType === 'discard'
        ? []
        : prevTempOptions => [...prevTempOptions!, newOption]
    )
  }

  function confirmSelectionsMadeByUser() {
    if (temporaryOptionsSelected) {
      dispatch({
        type: InputMultiSelectActions.ADD_OPTION,
        payload: [...temporaryOptionsSelected],
      })
    }

    setTemporaryOptionsSelected([])

    const formattedSelectedOptions = confirmedOptionsSelected.map(option => ({
      ...option,
      description: option.description ?? '',
    }))

    onSelectionChange?.(formattedSelectedOptions)
  }

  function removeTemporarySelectedOption(id?: string | number) {
    const idxTemporaryOptionSelected = temporaryOptionsSelected.findIndex(
      option => option.id === id
    )

    if (idxTemporaryOptionSelected > -1) {
      setTemporaryOptionsSelected(prevOption =>
        prevOption.filter(
          option =>
            option.id !==
            temporaryOptionsSelected[idxTemporaryOptionSelected].id
        )
      )
    }
  }

  function removeSelectedOption(
    id?: string | number,
    opts?: { clearAll: boolean }
  ) {
    if (opts?.clearAll) {
      dispatch({
        type: InputMultiSelectActions.CLEAR_OPTIONS,
      })

      setTemporaryOptionsSelected([])

      return
    }

    dispatch({
      type: InputMultiSelectActions.REMOVE_OPTION,
      payload: {
        id: id!,
      },
    })

    removeTemporarySelectedOption(id)
  }

  const handleControlFocusState = useCallback((state: 'focus' | 'blur') => {
    if (state === 'focus') {
      hiddenInputRef.current?.focus()
    } else {
      hiddenInputRef.current?.blur()
    }

    setIsFocused(state === 'focus')
  }, [])

  return (
    <InputMultiSelectProvider
      scope={__scopeInputMultiSelect}
      size={size}
      isFocused={isFocused}
      isError={isError}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      controlLayoutInfo={controlLayoutInfo}
      setControlLayoutInfo={setControlLayoutInfo}
      options={options}
      confirmedOptionsSelected={confirmedOptionsSelected}
      temporaryOptionsSelected={temporaryOptionsSelected}
      confirmSelectionsMadeByUser={confirmSelectionsMadeByUser}
      handleTempOptionSelected={handleTempOptionSelected}
      removeSelectedOption={removeSelectedOption}
      handleControlFocusState={handleControlFocusState}
      onSelectionChange={onSelectionChange}
    >
      <S.Container testID={testID} ref={ref} full={full} collapsable={false}>
        {otherElements}

        <S.HiddenInput
          {...props}
          ref={hiddenInputRef}
          accessible
          accessibilityElementsHidden
          editable={!isDisabled}
          readOnly={isReadOnly}
        />
      </S.Container>

      <InputMultiSelectPortal
        visible={isFocused}
        onOverlayPress={() => {
          handleControlFocusState('blur')
          setTemporaryOptionsSelected([])
        }}
      >
        <S.ClearButtonTouchableArea
          testID="input-multi-select-(clear-button)"
          style={{
            width: controlLayoutInfo.width / 8,
            height: controlLayoutInfo.height,

            top: controlLayoutInfo.y - controlLayoutInfo.height,
            right: controlLayoutInfo.x + 32,
          }}
          onPress={() =>
            removeSelectedOption(0, {
              clearAll: true,
            })
          }
        />

        {isFocused && clonedDropdownElement}
      </InputMultiSelectPortal>
    </InputMultiSelectProvider>
  )
})

InputMultiSelectRoot.displayName = DisplayNames.ROOT
